/**
 * React Query hooks for LinkedIn messages
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@lib/api';
import { useAuth } from '@lib/auth';
import type { MessagesResponse, MessageStatsResponse, LinkedInMessage, CurrentMessageResponse } from '@lib/types';

export const messageKeys = {
  all: ['messages'] as const,
  list: () => [...messageKeys.all, 'list'] as const,
  stats: () => [...messageKeys.all, 'stats'] as const,
};

export function useMessages() {
  const { isAuthenticated, isLoading } = useAuth();

  return useQuery({
    queryKey: messageKeys.list(),
    queryFn: () => api.get<MessagesResponse>('/api/messages'),
    staleTime: 1000 * 60,
    enabled: isAuthenticated && !isLoading,
  });
}

export function useMessageStats() {
  const { isAuthenticated, isLoading } = useAuth();

  return useQuery({
    queryKey: messageKeys.stats(),
    queryFn: () => api.get<MessageStatsResponse>('/api/messages/stats'),
    staleTime: 1000 * 60,
    enabled: isAuthenticated && !isLoading,
  });
}

export function useActiveMessages() {
  const { data, ...rest } = useMessages();
  return {
    ...rest,
    data: data?.messages.filter(m => m.isActive) ?? [],
  };
}

export function useCurrentMessage() {
  const { isAuthenticated, isLoading } = useAuth();

  return useQuery({
    queryKey: [...messageKeys.all, 'current'] as const,
    queryFn: () => api.get<CurrentMessageResponse>('/api/messages/current'),
    staleTime: 1000 * 60,
    enabled: isAuthenticated && !isLoading,
  });
}

export function useSetCurrentMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.put<{ success: boolean }>(`/api/messages/${id}/current`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

export function useCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { title: string; content: string }) =>
      api.post<LinkedInMessage>('/api/messages', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { 
      id: string; 
      title?: string; 
      content?: string; 
      isActive?: boolean;
      createNewVersion?: boolean;
    }) =>
      api.put<{ updated: boolean; newVersion?: boolean; newId?: string }>(`/api/messages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/messages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}
